import React from 'react';
import { useCommunityDevMode } from './CommunityDevModeContext';
import { LS, MARKET_PROFILE_DEV_PRESET_LABELS, type MarketProfileDevPreset } from './communityDevMode';

const MARKET_PROFILE_PRESETS: MarketProfileDevPreset[] = ['empty', 'pending_review', 'access_granted'];

export function CommunityDevToggle() {
  const { isDevUser, devMode, toggleDevMode } = useCommunityDevMode();
  if (!isDevUser) return null;

  return (
    <button
      type="button"
      className={`dc-dev-toggle${devMode ? ' dc-dev-toggle-on' : ''}`}
      onClick={toggleDevMode}
      title={devMode ? 'Скрыть пути хранения' : 'Показать пути хранения'}
      aria-pressed={devMode}
    >
      <i className="fas fa-code text-xs" aria-hidden="true" />
      <span>{devMode ? 'Dev ON' : 'Dev'}</span>
    </button>
  );
}

export function CommunityPreviewToggle() {
  const { isDevUser, devMode, previewAsUser, setPreviewAsUser } = useCommunityDevMode();
  if (!isDevUser || !devMode) return null;

  return (
    <div className="dcf-dev-preview-toggle" role="group" aria-label="Режим просмотра">
      <button
        type="button"
        className={`dcf-dev-preview-toggle__btn ${!previewAsUser ? 'dcf-dev-preview-toggle__btn--active' : ''}`}
        onClick={() => setPreviewAsUser(false)}
      >
        Админ
      </button>
      <button
        type="button"
        className={`dcf-dev-preview-toggle__btn ${previewAsUser ? 'dcf-dev-preview-toggle__btn--active' : ''}`}
        onClick={() => setPreviewAsUser(true)}
      >
        Пользователь
      </button>
    </div>
  );
}

export function MarketProfileDevPresetToggle() {
  const {
    isDevUser,
    devMode,
    marketProfilePreset,
    applyMarketProfilePreset,
  } = useCommunityDevMode();
  if (!isDevUser || !devMode) return null;

  return (
    <div
      className="dcf-dev-preview-toggle dcf-dev-mp-preset-toggle"
      role="group"
      aria-label="Состояние профиля рынка"
    >
      {MARKET_PROFILE_PRESETS.map((preset) => (
        <button
          key={preset}
          type="button"
          className={`dcf-dev-preview-toggle__btn ${
            marketProfilePreset === preset ? 'dcf-dev-preview-toggle__btn--active' : ''
          }`}
          onClick={() => applyMarketProfilePreset(preset)}
          title={`Профиль рынка: ${MARKET_PROFILE_DEV_PRESET_LABELS[preset]}`}
        >
          {MARKET_PROFILE_DEV_PRESET_LABELS[preset]}
        </button>
      ))}
    </div>
  );
}

export function CommunityDevBar({ realIsAdmin }: { realIsAdmin: boolean }) {
  const { isDevUser, devMode, effectiveIsAdmin } = useCommunityDevMode();
  if (!isDevUser) return null;

  const shownAsAdmin = effectiveIsAdmin(realIsAdmin);

  return (
    <div className="dcf-dev-bar">
      <CommunityDevToggle />
      <CommunityPreviewToggle />
      <MarketProfileDevPresetToggle />
      {devMode && (
        <span className="dcf-dev-bar__hint">
          UI как <strong>{shownAsAdmin ? 'администратор' : 'участник'}</strong>
          {realIsAdmin !== shownAsAdmin ? ' (симуляция)' : ''}
          {' · '}
          <code className="dcf-dev-bar__code">{LS}</code>
        </span>
      )}
    </div>
  );
}

export function CommunityDevPath({
  path,
  inline,
  className = '',
}: {
  path: string;
  inline?: boolean;
  className?: string;
}) {
  const { devMode } = useCommunityDevMode();
  if (!devMode || !path) return null;
  if (inline) {
    return <span className={`dc-dev-field-name-inline ${className}`.trim()}>{path}</span>;
  }
  return <div className={`dc-dev-field-name ${className}`.trim()}>{path}</div>;
}

export function CommunityDevField({
  label,
  storagePath,
  children,
  hint,
  htmlFor,
  className = '',
}: {
  label: string;
  storagePath?: string;
  children: React.ReactNode;
  hint?: string;
  htmlFor?: string;
  className?: string;
}) {
  const Tag = htmlFor ? 'label' : 'span';
  return (
    <div className={`dcf-field ${className}`.trim()}>
      <Tag className="dcf-field__label" {...(htmlFor ? { htmlFor } : {})}>
        {label}
        {storagePath ? <CommunityDevPath path={storagePath} inline /> : null}
      </Tag>
      {children}
      {hint && <p className="dcf-field__hint">{hint}</p>}
    </div>
  );
}

export function CommunityDevTh({
  label,
  storagePath,
}: {
  label: string;
  storagePath?: string;
}) {
  return (
    <th>
      {storagePath ? <CommunityDevPath path={storagePath} /> : null}
      {label}
    </th>
  );
}

export function CommunityDevRowMeta({ parts }: { parts: (string | false | null | undefined)[] }) {
  const { devMode } = useCommunityDevMode();
  const line = parts.filter(Boolean).join(' · ');
  if (!devMode || !line) return null;
  return <div className="dc-dev-field-name mt-0.5">{line}</div>;
}

/** Обёртка сущности (тема, сообщение, пункт навигации) */
export function CommunityDevEntity({
  storagePath,
  children,
  className = '',
}: {
  storagePath: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <CommunityDevPath path={storagePath} />
      {children}
    </div>
  );
}

/**
 * Admin-only UI: в режиме «Пользователь» показывает призрачную копию,
 * чтобы было видно, что скрыто от обычного участника.
 */
export function CommunityDevAdminGate({
  realIsAdmin,
  isAdmin,
  label,
  storagePath,
  children,
  className = '',
}: {
  realIsAdmin: boolean;
  isAdmin: boolean;
  label: string;
  storagePath?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { devMode, previewAsUser } = useCommunityDevMode();

  if (isAdmin) {
    return (
      <>
        {devMode && storagePath ? (
          <CommunityDevPath path={`${storagePath} · только админ`} />
        ) : null}
        {children}
      </>
    );
  }

  if (devMode && previewAsUser && realIsAdmin) {
    return (
      <div className={`dcf-dev-admin-ghost ${className}`.trim()} aria-hidden="true">
        <div className="dc-dev-field-name">
          скрыто для пользователя · {label}
          {storagePath ? ` · ${storagePath}` : ''}
        </div>
        <div className="dcf-dev-admin-ghost__body">{children}</div>
      </div>
    );
  }

  return null;
}
